import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDataFetchingClient, useQuery } from 'data-fetching-client';
import { CREATE_BUYER_OVERRIDES_SUBTOTALS, CREATE_BUYER_OVERRIDES_PREVIEW_SUBTOTALS } from '../data/queries';
import addGenericFloatingError from '../utils/addGenericFloatingError';
import updateCheckoutSessionSubtotals from '../utils/updateCheckoutSessionSubtotals';
import BuyerOverridesActionTypes from '../state/BuyerOverrides/actionTypes';
import { getBuyerOverridesLineItems, getBuyerOverridesPartialPaymentAmount } from '../state/BuyerOverrides/selectors';
import { usePaymentFormContext } from '../context/PaymentFormContext';
import useCalculateTax, { getShouldCalculateTaxOnSubtotalChange } from './useCalculateTax';
import { fetchBuyerOverridesSubtotals } from '../data/BuyerOverridesApi';
import { getTaxCalculationId, setCalculateTaxLoading } from '../state/calculateTaxSlice';
import { useInitializePartialPaymentAmount } from './useInitializePartialPaymentAmount';
function useUpdateBuyerOverridesLoading(results) {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch({
      type: BuyerOverridesActionTypes.SET_LOADING,
      data: {
        isLoading: results.loading
      }
    });
  }, [dispatch, results.loading]);
}
function hasUnsetQuantityLineItems(buyerOverridesLineItems) {
  return Boolean(buyerOverridesLineItems === null || buyerOverridesLineItems === void 0 ? void 0 : buyerOverridesLineItems.find(lineItem => 'quantity' in lineItem && lineItem.quantity === 0));
}
// for the checkout page, after we have a checkoutSessionId from an existing payment link
export function useFetchDynamicSubtotals({
  checkoutSession,
  skip = false,
  paymentMethod
}) {
  var _checkoutSession$part, _checkoutSession$subt;
  const client = useDataFetchingClient();
  const buyerOverridesLineItems = useSelector(getBuyerOverridesLineItems);
  const shouldSkipForUnsetQuantities = hasUnsetQuantityLineItems(buyerOverridesLineItems);
  useInitializePartialPaymentAmount({
    checkoutSession,
    selectedPaymentMethod: paymentMethod
  });
  const partialPaymentAmount = useSelector(getBuyerOverridesPartialPaymentAmount);
  const isPartialPaymentsEnabled = (_checkoutSession$part = checkoutSession.partialPaymentsInfo) === null || _checkoutSession$part === void 0 ? void 0 : _checkoutSession$part.isPartialPaymentsEnabled;
  const shouldSkipForPartialPaymentAmount = Boolean(isPartialPaymentsEnabled) && partialPaymentAmount === undefined;
  const result = useQuery(CREATE_BUYER_OVERRIDES_SUBTOTALS, {
    skip: skip || shouldSkipForUnsetQuantities || shouldSkipForPartialPaymentAmount,
    variables: {
      buyerOverrides: {
        discountCode: (_checkoutSession$subt = checkoutSession.subtotalsPublicResponse.discountCodeResponse) === null || _checkoutSession$subt === void 0 ? void 0 : _checkoutSession$subt.discountCode,
        lineItems: buyerOverridesLineItems,
        paymentAmount: partialPaymentAmount
      },
      paymentSessionId: checkoutSession.id,
      paymentMethod
    },
    onCompleted: ({
      subtotals
    }) => {
      updateCheckoutSessionSubtotals(client, subtotals);
    },
    onError: addGenericFloatingError
  });
  useUpdateBuyerOverridesLoading(result);
  return result;
}
export function useFetchDynamicSubtotalsWithTax({
  checkoutSession,
  skip = false,
  paymentMethod
}) {
  var _checkoutSession$subt2;
  const [result, setResult] = useState({
    data: undefined,
    loading: false,
    error: undefined
  });
  const client = useDataFetchingClient();
  const dispatch = useDispatch();
  const currentTaxCalculationId = useSelector(getTaxCalculationId);
  const {
    callCalculateTax
  } = useCalculateTax();
  const {
    formState
  } = usePaymentFormContext();
  const buyerOverridesLineItems = useSelector(getBuyerOverridesLineItems);
  const shouldSkipForUnsetQuantities = hasUnsetQuantityLineItems(buyerOverridesLineItems);
  const shouldSkip = skip || shouldSkipForUnsetQuantities;
  const isAutomatedSalesTaxEnabled = checkoutSession.isAutomatedSalesTaxEnabled;
  const billingPostalCode = formState.billingAddress.postalCode;
  const shippingPostalCode = formState.shippingAddress.postalCode;
  const billingCountry = formState.billingAddress.country;
  const shippingCountry = formState.shippingAddress.country;
  const checkoutSessionId = checkoutSession.id;
  const discountCode = (_checkoutSession$subt2 = checkoutSession.subtotalsPublicResponse.discountCodeResponse) === null || _checkoutSession$subt2 === void 0 ? void 0 : _checkoutSession$subt2.discountCode;

  // there are two useEffects here:
  // 1. when the taxCalculationId changes, we fetch the subtotals
  // 2. when the buyerOverridesLineItems change, we calculate tax if needed and fetch the subtotals

  useEffect(() => {
    async function fetchTaxAndSubtotals() {
      // need to use undefined here because redux state uses null and API expects undefined
      let taxCalculationId = currentTaxCalculationId || undefined;
      try {
        const shouldCalculateTax = getShouldCalculateTaxOnSubtotalChange({
          isAutomatedSalesTaxEnabled,
          billingPostalCode,
          shippingPostalCode,
          billingCountry,
          shippingCountry
        });
        if (shouldCalculateTax) {
          dispatch(setCalculateTaxLoading(true));
          taxCalculationId = await callCalculateTax();
        }
        setResult({
          data: undefined,
          loading: true,
          error: undefined
        });
        const subtotalsResponse = await fetchBuyerOverridesSubtotals({
          buyerOverrides: {
            discountCode,
            lineItems: buyerOverridesLineItems
          },
          paymentSessionId: checkoutSessionId,
          taxCalculationId,
          paymentMethod
        });
        updateCheckoutSessionSubtotals(client, subtotalsResponse);
        setResult({
          data: {
            subtotals: subtotalsResponse
          },
          loading: false,
          error: undefined
        });
      } catch (error) {
        setResult({
          data: undefined,
          loading: false,
          error
        });
        addGenericFloatingError();
      } finally {
        dispatch(setCalculateTaxLoading(false));
      }
    }
    if (!shouldSkip) {
      void fetchTaxAndSubtotals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerOverridesLineItems]);
  useEffect(() => {
    async function fetchSubtotals() {
      try {
        setResult({
          data: undefined,
          loading: true,
          error: undefined
        });
        const subtotalsResponse = await fetchBuyerOverridesSubtotals({
          buyerOverrides: {
            discountCode,
            lineItems: buyerOverridesLineItems
          },
          paymentSessionId: checkoutSessionId,
          taxCalculationId: currentTaxCalculationId || undefined,
          paymentMethod
        });
        updateCheckoutSessionSubtotals(client, subtotalsResponse);
        setResult({
          data: {
            subtotals: subtotalsResponse
          },
          loading: false,
          error: undefined
        });
      } catch (error) {
        setResult({
          data: undefined,
          loading: false,
          error
        });
        addGenericFloatingError();
      } finally {
        dispatch(setCalculateTaxLoading(false));
      }
    }
    if (!shouldSkip) {
      void fetchSubtotals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTaxCalculationId, paymentMethod]);
  return result;
}
// for the preview iframe, before we have a checkoutSessionId because we're creating a payment link
export function useFetchDynamicSubtotalsPreview({
  checkoutSession,
  discount,
  adjustments,
  portalId,
  currencyCode,
  lineItems,
  skip = false,
  paymentMethod,
  checkoutFees
}) {
  var _checkoutSession$part2, _checkoutSession$subt3;
  const client = useDataFetchingClient();
  const buyerOverridesLineItems = useSelector(getBuyerOverridesLineItems);
  const shouldSkipForUnsetQuantities = hasUnsetQuantityLineItems(buyerOverridesLineItems);
  useInitializePartialPaymentAmount({
    checkoutSession,
    selectedPaymentMethod: paymentMethod
  });
  const partialPaymentAmount = useSelector(getBuyerOverridesPartialPaymentAmount);
  const isPartialPaymentsEnabled = (_checkoutSession$part2 = checkoutSession.partialPaymentsInfo) === null || _checkoutSession$part2 === void 0 ? void 0 : _checkoutSession$part2.isPartialPaymentsEnabled;
  const shouldSkipForPartialPaymentAmount = Boolean(isPartialPaymentsEnabled) && partialPaymentAmount === undefined;
  const result = useQuery(CREATE_BUYER_OVERRIDES_PREVIEW_SUBTOTALS, {
    skip: skip || shouldSkipForUnsetQuantities || shouldSkipForPartialPaymentAmount,
    variables: {
      buyerOverrides: {
        discountCode: (_checkoutSession$subt3 = checkoutSession.subtotalsPublicResponse.discountCodeResponse) === null || _checkoutSession$subt3 === void 0 ? void 0 : _checkoutSession$subt3.discountCode,
        lineItems: buyerOverridesLineItems,
        paymentAmount: partialPaymentAmount
      },
      currencyCode,
      lineItems,
      portalId,
      adjustments,
      discount,
      paymentMethod,
      checkoutFees
    },
    onCompleted: ({
      subtotals
    }) => updateCheckoutSessionSubtotals(client, subtotals),
    onError: addGenericFloatingError
  });
  useUpdateBuyerOverridesLoading(result);
  return result;
}